// Admin settings 前端 hooks

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from './api.js';

import type { AiAutoSummaryDTO, EncryptionStatusDTO, GmailPrimaryDTO, TrackingProtectionDTO } from '@aliashub/shared/dto';

const KEY = ['settings', 'gmail-primary'] as const;

export function useGmailPrimary() {
  return useQuery({
    queryKey: KEY,
    queryFn: () => api.get<GmailPrimaryDTO>('/api/v1/settings/gmail-primary'),
    staleTime: 60_000,
  });
}

export function useSetGmailPrimary() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (address: string) => api.post<GmailPrimaryDTO>('/api/v1/settings/gmail-primary', { address }),
    onSuccess: (data) => qc.setQueryData(KEY, data),
  });
}

const ENCRYPTION_KEY = ['settings', 'encryption'] as const;

export function useEncryptionStatus() {
  return useQuery({
    queryKey: ENCRYPTION_KEY,
    queryFn: () => api.get<EncryptionStatusDTO>('/api/v1/settings/encryption/status'),
    staleTime: 30_000,
  });
}

export function useSetEncryptionEnabled() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (enabled: boolean) => api.post<EncryptionStatusDTO>('/api/v1/settings/encryption', { enabled }),
    onSuccess: (data) => qc.setQueryData(ENCRYPTION_KEY, data),
  });
}

const AI_SUMMARY_KEY = ['settings', 'ai-summary'] as const;

export function useAiAutoSummary() {
  return useQuery({
    queryKey: AI_SUMMARY_KEY,
    queryFn: () => api.get<AiAutoSummaryDTO>('/api/v1/settings/ai-summary'),
    staleTime: 60_000,
  });
}

export function useSetAiAutoSummary() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (enabled: boolean) => api.post<AiAutoSummaryDTO>('/api/v1/settings/ai-summary', { enabled }),
    onSuccess: (data) => qc.setQueryData(AI_SUMMARY_KEY, data),
  });
}

const TRACKING_PROTECTION_KEY = ['settings', 'tracking-protection'] as const;

export function useTrackingProtection() {
  return useQuery({
    queryKey: TRACKING_PROTECTION_KEY,
    queryFn: () => api.get<TrackingProtectionDTO>('/api/v1/settings/tracking-protection'),
    staleTime: 60_000,
  });
}

export function useSetTrackingProtection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (enabled: boolean) =>
      api.post<TrackingProtectionDTO>('/api/v1/settings/tracking-protection', { enabled }),
    onSuccess: (data) => qc.setQueryData(TRACKING_PROTECTION_KEY, data),
  });
}
